// ---------------------------------------------------------------------------
// Step Inspector — right panel showing selected step parameters in editable form.
//
// Reads selectedStepIndex + selectedFlowId from the store, resolves the step
// from block data, and renders the appropriate form based on action/step type.
// ---------------------------------------------------------------------------

import { useMemo, useState } from 'react'
import {
  Trash2,
  MousePointerClick,
  Database,
  ArrowRight,
  Zap,
  Variable,
  MessageSquare,
  GitBranch,
  GitFork,
  Repeat,
  Shield,
  Code,
  Play,
  Plus,
  X,
  Layers,
} from 'lucide-react'
import type { LucideIcon } from 'lucide-react'
import type { FlowStep, BasicStep, ConditionalStep, LoopStep, SwitchStep, TryCatchStep, ParallelStep, StageStep, SagaStep, Flow } from '@cogo/types-v2'
import { ACTION_DEFS } from '@cogo/action-registry'
import type { ActionParamDef } from '@cogo/action-registry'
import { useFlowEditorStore } from '@/features/flow-editor/store/flow-editor-store'
import { useStudioStore } from '@/store/studio-store'
import { cn } from '@/lib/utils'
import { FORM, INSPECTOR, ICON } from '@/features/flow-editor/lib/design-tokens'
import { VariableAutocomplete } from '@/features/flow-editor/components/inspector/forms/variable-autocomplete'
import { useOwningBlockVariableNames } from '@/features/flow-editor/hooks/use-symbol-suggestions'
import { validateBareTargetName } from '@/features/flow-editor/lib/expression-prefixes'
import {
  resolveLegacyActionAlias,
  legacyRuntimeStatus,
} from '@/features/flow-editor/lib/legacy-action-aliases'
import { dispatchFlowStepsOp } from '@/features/flow-editor/lib/flow-steps-dispatch'
import { toast } from '@/store/toast-store'
import { SHORTCUT } from '@/lib/shortcuts'
import {
  countDescendantSteps,
  getNestedSteps,
  resolveStepAtPath,
} from '@/features/flow-editor/store/flow-slice'
import type { StepPath } from '@/features/flow-editor/store/flow-slice'

// ---------------------------------------------------------------------------
// Branch-add stub
// ---------------------------------------------------------------------------

// Exported for the catalog<->runtime parity gate (add-step-popover-parity.test.ts):
// the stub MUST name a canonical runtime action from ACTION_DEFS. The previous
// 'toast_action' stub was a phantom name the runtime silently skipped (F35).
export function makeConditionBranchStubStep(): BasicStep {
  return { type: 'basic', action: 'showToast', params: { message: 'new branch step' } }
}

// ---------------------------------------------------------------------------
// Category helpers
// ---------------------------------------------------------------------------

const ACTION_CATEGORY: Record<string, string> = {
  setState: 'State',
  compute: 'Data',
  upsertWhere: 'Data',
  removeWhere: 'Data',
  navigate: 'Navigation',
  scrollToPosition: 'Navigation',
  restful_data_action: 'API',
  restApi: 'API',
  graphql_action: 'API',
  supabase_select: 'Database',
  supabase_insert: 'Database',
  supabase_update: 'Database',
  supabase_delete: 'Database',
  showToast: 'UI',
  show_popup_action: 'UI',
  open_drawer_action: 'UI',
  callFlow: 'Flow',
  expression: 'Logic',
  'domain-operation': 'API',
}

const CATEGORY_COLORS: Record<string, string> = {
  State: 'bg-blue-500/15 text-blue-600 dark:text-blue-400',
  Data: 'bg-primary/15 text-primary dark:text-primary',
  Navigation: 'bg-orange-500/15 text-orange-600 dark:text-orange-400',
  API: 'bg-violet-500/15 text-violet-600 dark:text-violet-400',
  Database: 'bg-cyan-500/15 text-cyan-600 dark:text-cyan-400',
  UI: 'bg-pink-500/15 text-pink-600 dark:text-pink-400',
  Flow: 'bg-indigo-500/15 text-indigo-600 dark:text-indigo-400',
  Logic: 'bg-amber-500/15 text-amber-600 dark:text-amber-400',
  Control: 'bg-gray-500/15 text-gray-600 dark:text-gray-400',
}

const ACTION_ICONS: Record<string, LucideIcon> = {
  setState: Database,
  compute: Variable,
  navigate: ArrowRight,
  scrollToPosition: ArrowRight,
  restApi: Zap,
  restful_data_action: Zap,
  showToast: MessageSquare,
  conditional: GitBranch,
  tryCatch: Shield,
  loop: Repeat,
  switch: GitFork,
  stage: Layers,
  saga: Shield,
  expression: Code,
  callFlow: Play,
  upsertWhere: Database,
  removeWhere: Database,
  'domain-operation': Zap,
}

function getStepLabel(step: FlowStep): string {
  if ('action' in step) return (step as BasicStep).action
  return step.type
}

function getStepCategory(step: FlowStep): string {
  if ('action' in step) {
    return ACTION_CATEGORY[(step as BasicStep).action] ?? 'Action'
  }
  if (isDomainOperationStep(step)) return ACTION_CATEGORY['domain-operation']
  return 'Control'
}

function isDomainOperationStep(step: FlowStep): boolean {
  const record = step as unknown as Record<string, unknown>
  return record.type === 'domain-operation' || record.action === 'domain-operation'
}

function isSourceTypedActionStep(step: FlowStep): boolean {
  const record = step as unknown as Record<string, unknown>
  return (
    record.type === 'navigate' ||
    record.type === 'setState' ||
    record.type === 'callback' ||
    record.type === 'event'
  )
}

// ---------------------------------------------------------------------------
// Flow owner lookup (same logic as FlowStepPanel)
// ---------------------------------------------------------------------------

function findFlowOwner(
  blocks: Record<string, { flow?: Record<string, unknown> }>,
  flowName: string,
): { blockName: string; flow: Flow } | null {
  for (const [blockName, block] of Object.entries(blocks)) {
    if (!block.flow) continue
    if (block.flow[flowName] && typeof block.flow[flowName] === 'object') {
      return { blockName, flow: block.flow[flowName] as Flow }
    }
    const flows = block.flow.flows as Record<string, Flow> | undefined
    if (flows && flows[flowName]) {
      return { blockName, flow: flows[flowName] }
    }
  }
  return null
}

/** A saga must retain one stage, so its sole stage is not a deletable step. */
export function isProtectedSoleSagaStage(steps: FlowStep[], path: StepPath | null): boolean {
  if (path == null || path.length < 3 || path[path.length - 2] !== 'stages') return false
  const parentPath = path.slice(0, -2) as StepPath
  const parent = resolveStepAtPath(steps, parentPath)
  return parent?.type === 'saga' && parent.stages.length === 1
}

// ---------------------------------------------------------------------------
// Per-type form components
// ---------------------------------------------------------------------------

interface StepFormProps {
  step: FlowStep
  onChange: (params: Record<string, unknown>) => void
  /** Patches typed-step TOP-LEVEL fields (condition / collection / itemVar /
   * value) — routed to updateStepFields. Basic-step params go via onChange. */
  onFieldsChange?: (patch: Record<string, unknown>) => void
  /** Switch-case key management — routed to addSwitchCase / removeSwitchCase. */
  onAddCase?: (caseKey: string) => void
  onRenameCase?: (oldKey: string, newKey: string) => void
  onRemoveCase?: (caseKey: string) => void
  /**
   * QA-4 edit-time legacy migration — routed to migrateStepAction. When the
   * step's action is a known legacy alias, the dispatcher rewires the form's
   * onChange through this so a REAL user commit lands the canonical action
   * name + canonical param shape atomically.
   */
  onMigrate?: (canonicalAction: string, params: Record<string, unknown>) => void
}

function SetStateStepForm({ step, onChange }: StepFormProps) {
  const params = (step as BasicStep).params
  // Seed from the canonical param first, then legacy aliases (key/path).
  const initialTarget =
    (params.target as string) ?? (params.key as string) ?? (params.path as string) ?? ''
  const [target, setTarget] = useState(initialTarget)
  const [value, setValue] = useState((params.value as string) ?? '')
  const targetNames = useOwningBlockVariableNames()
  const targetError = validateBareTargetName(target)

  // Steps persisted with only the legacy key/path alias LOOK configured but
  // crash at runtime: actionSetState reads STRICT params.target. The old form
  // healed them on mere blur — keep that one case by relaxing the no-op guard,
  // but ONLY for canonically-named steps. Legacy-NAMED steps (set_state_action,
  // QA-4) reach this form through the alias dispatcher and must obey the
  // strict no-op-blur discipline: tab-through never mutates; migration to
  // {action: setState, params: {target}} happens only on a real edit (or the
  // inspector's explicit "Update to current format" affordance).
  const isLegacyNamed = (step as BasicStep).action !== 'setState'
  const seededFromAlias = params.target === undefined && initialTarget !== '' && !isLegacyNamed

  const commitTarget = () => {
    // Invalid input is never committed — the runtime would interpolate a
    // #{}-wrapped target as a VALUE and silently write to a garbage key
    // (F44), and the validator skips targets containing `#{`.
    if (targetError) return
    // Changed-vs-step guard (see ConditionalStepForm): no typing → no emit —
    // EXCEPT when the displayed value came from a legacy alias, where the
    // unchanged blur commits the canonical shape (migrate-on-blur).
    if (target === initialTarget && !seededFromAlias) return
    if (target === '') return
    // Single canonical param: runtime-v2 setState reads STRICT params.target.
    // Stale key/path aliases are dropped so they cannot diverge.
    const { key: _key, path: _path, ...rest } = params
    onChange({ ...rest, target })
  }

  return (
    <div className="space-y-2">
      <div>
        <label className={FORM.labelClass}>Target <span className="text-rose-400">*</span></label>
        <VariableAutocomplete
          mode="key-only"
          value={target}
          onChange={(v) => setTarget(v)}
          onBlur={commitTarget}
          placeholder="email"
          testid="param-input-key"
          suggestions={targetNames}
        />
        {targetError ? (
          <div data-testid="setstate-target-error" className="mt-0.5 text-[9px] text-destructive">
            {targetError}
          </div>
        ) : (
          <div className="mt-0.5 text-[9px] text-muted-foreground/70">
            Bare variable name, no <span className="font-mono">#{'{}'}</span> — e.g.{' '}
            <span className="font-mono">email</span> (block state) or{' '}
            <span className="font-mono">appData.cart</span> (app state).
          </div>
        )}
      </div>
      <div>
        <label className={FORM.labelClass}>Value <span className="text-rose-400">*</span></label>
        <VariableAutocomplete
          mode="expression"
          value={value}
          onChange={(v) => setValue(v)}
          onBlur={() => { if (value !== ((params.value as string) ?? '')) onChange({ ...params, value }) }}
          placeholder="#{local.email} or literal"
          testid="param-input-value"
        />
      </div>
    </div>
  )
}

function NavigateStepForm({ step, onChange }: StepFormProps) {
  const params = (step as BasicStep).params
  const initialRoute = (params.route as string) ?? (params.target as string) ?? ''
  const [route, setRoute] = useState(initialRoute)

  return (
    <div className="space-y-2">
      <FormField label="Route" required value={route}
        onChange={(v) => setRoute(v)}
        // No-op blur emits NOTHING (#969/#970 discipline) — required for the
        // QA-4 aliased seed path, where any emit would migrate the step.
        onBlur={() => { if (route !== initialRoute) onChange({ ...params, route }) }}
        placeholder="/page-name" />
    </div>
  )
}

function ComputeStepForm({ step, onChange }: StepFormProps) {
  const params = (step as BasicStep).params
  const initialTarget = (params.target as string) ?? ''
  const [target, setTarget] = useState(initialTarget)
  const [expression, setExpression] = useState((params.expression as string) ?? (params.value as string) ?? '')

  const emit = (patch: Record<string, unknown>) => {
    onChange({ ...params, ...patch })
  }

  return (
    <div className="space-y-2">
      <BareTargetField
        initialTarget={initialTarget}
        target={target}
        setTarget={setTarget}
        onCommit={() => emit({ target })}
        testid="param-input-target"
        errorTestid="compute-target-error"
      />
      <div>
        <label className={FORM.labelClass}>Expression <span className="text-rose-400">*</span></label>
        <VariableAutocomplete
          mode="expression"
          value={expression}
          onChange={(v) => setExpression(v)}
          onBlur={() => emit({ expression })}
          placeholder="#{local.price} * #{local.qty}"
        />
      </div>
    </div>
  )
}

/**
 * Shared Target field for the steps whose target is a BARE write path
 * (compute / expression route to `state.setState(target)` exactly like
 * setState — the same F44 silent-garbage-key class applies).
 */
function BareTargetField({ initialTarget, target, setTarget, onCommit, testid, errorTestid, placeholder }: {
  initialTarget: string
  target: string
  setTarget: (v: string) => void
  onCommit: () => void
  testid: string
  errorTestid: string
  placeholder?: string
}) {
  const targetNames = useOwningBlockVariableNames()
  const targetError = validateBareTargetName(target)

  const commitTarget = () => {
    if (targetError) return
    if (target === initialTarget) return
    if (target === '') return
    onCommit()
  }

  return (
    <div>
      <label className={FORM.labelClass}>Target <span className="text-rose-400">*</span></label>
      <VariableAutocomplete
        mode="key-only"
        value={target}
        onChange={setTarget}
        onBlur={commitTarget}
        placeholder={placeholder ?? 'result'}
        testid={testid}
        suggestions={targetNames}
      />
      {targetError && (
        <div data-testid={errorTestid} className="mt-0.5 text-[9px] text-destructive">
          {targetError}
        </div>
      )}
    </div>
  )
}

function ShowToastStepForm({ step, onChange }: StepFormProps) {
  const params = (step as BasicStep).params
  const initialMessage = (params.message as string) ?? ''
  const [message, setMessage] = useState(initialMessage)
  const [variant, setVariant] = useState((params.variant as string) ?? 'info')

  const emit = (patch: Record<string, unknown>) => {
    onChange({ ...params, ...patch })
  }

  return (
    <div className="space-y-2">
      <FormField label="Message" required value={message}
        onChange={(v) => setMessage(v)}
        // No-op blur emits NOTHING (#969/#970 discipline; QA-4 aliased path).
        onBlur={() => { if (message !== initialMessage) emit({ message }) }}
        placeholder="Item added to cart" />
      <div>
        <label className={FORM.labelClass}>Variant</label>
        <select
          className="w-full mt-0.5 text-xs border border-border rounded px-2 py-1 bg-background"
          value={variant}
          onChange={(e) => { setVariant(e.target.value); emit({ variant: e.target.value }) }}
        >
          {['info', 'success', 'warning', 'error'].map((v) => (
            <option key={v} value={v}>{v}</option>
          ))}
        </select>
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// upsertWhere / removeWhere / updateWhere — canonical write contracts (F2).
//
// The runtime engine executes these standalone actions through the setState
// alias layer (@cogo/runtime-v2 action-flow-engine.ts:205-207) and reads ONLY
//   upsertWhere : target + matchExpr + onMatch + onNew
//   removeWhere : target + predicate
//   updateWhere : target + predicate + updates  (GenericStepForm via ACTION_DEFS)
// The pre-F2 forms emitted {collection,match,where,data,value} — names the
// engine never reads — so persisted steps silently no-op'd in Preview while
// tracing 'ok'. The forms below READ the legacy keys tolerantly and WRITE
// canonical-only; form-engine-param-contract.test.ts runs these key sets
// through the REAL engine and asserts the state mutation happens.
// ---------------------------------------------------------------------------

export const UPSERT_WHERE_FORM_PARAMS = ['target', 'matchExpr', 'onMatch', 'onNew'] as const
export const REMOVE_WHERE_FORM_PARAMS = ['target', 'predicate'] as const
export const UPDATE_WHERE_FORM_PARAMS = ['target', 'predicate', 'updates'] as const

/** Stale vocabulary the pre-F2 forms wrote — dropped from every commit. */
const WHERE_FORM_LEGACY_KEYS = ['collection', 'match', 'where', 'data', 'value'] as const

/**
 * Read-tolerance → write-canonical migration: drop the legacy keys and, when
 * a canonical key is absent, surface the legacy value into it so a commit of
 * ANY field heals the whole step at once (edit-time normalization — single
 * step, never bulk; QA-4 #978 philosophy). Untouched keys come from `params`
 * (committed truth), never from string shadows.
 */
function canonicalizeWhereParams(
  params: Record<string, unknown>,
  legacyFallbacks: Record<string, readonly string[]>,
): Record<string, unknown> {
  const rest: Record<string, unknown> = { ...params }
  for (const key of WHERE_FORM_LEGACY_KEYS) delete rest[key]
  for (const [canonical, fallbacks] of Object.entries(legacyFallbacks)) {
    if (rest[canonical] !== undefined) continue
    for (const legacy of fallbacks) {
      const v = params[legacy]
      if (typeof v === 'string' && v !== '') {
        rest[canonical] = v
        break
      }
    }
  }
  return rest
}

/**
 * COR-5 — legacy upsert payload migration (read-tolerant). Pre-F2 forms
 * persisted the upsert payload under `data`/`value` — keys the engine never
 * reads — and the write-canonical commit would otherwise silently DESTROY
 * that user-authored content without ever displaying it. When BOTH canonical
 * keys are absent, surface the legacy payload: an object (or a JSON-parseable
 * string) seeds BOTH onMatch and onNew — the legacy form semantics were
 * "upsert this data", which applies to either branch — and a non-JSON string
 * falls back to the raw editor draft so nothing is hidden (the user sees and
 * can fix exactly what was stored; it is never committed unparsed).
 */
function readLegacyUpsertPayload(params: Record<string, unknown>): {
  object?: Record<string, unknown>
  raw?: string
} {
  if (params.onMatch !== undefined || params.onNew !== undefined) return {}
  const legacy = params.data ?? params.value
  if (legacy != null && typeof legacy === 'object' && !Array.isArray(legacy)) {
    return { object: structuredClone(legacy) as Record<string, unknown> }
  }
  if (typeof legacy === 'string' && legacy.trim() !== '') {
    try {
      const parsed: unknown = JSON.parse(legacy)
      if (parsed != null && typeof parsed === 'object' && !Array.isArray(parsed)) {
        return { object: parsed as Record<string, unknown> }
      }
    } catch {
      // Not JSON — fall through to the visible raw-draft path.
    }
    return { raw: legacy }
  }
  return {}
}

/**
 * Parse-gated JSON object editor for dedicated forms (same F37/F46 guard as
 * GenericStepForm's jsonDrafts: the draft only commits when it parses to an
 * object — never String()-coerce, never commit a scalar).
 */
function JsonObjectField({ label, draft, error, testid, onDraftChange, onCommit, placeholder }: {
  label: string
  draft: string
  error?: string
  testid: string
  onDraftChange: (v: string) => void
  onCommit: () => void
  placeholder?: string
}) {
  return (
    <div>
      <label className={FORM.labelClass}>
        {label} <span className="text-[9px] text-muted-foreground/70">(JSON)</span>
      </label>
      <textarea
        className="w-full mt-0.5 text-xs border border-border rounded px-2 py-1 bg-background font-mono min-h-[60px] resize-y"
        value={draft}
        onChange={(e) => onDraftChange(e.target.value)}
        onBlur={onCommit}
        placeholder={placeholder ?? '{ "key": "value" }'}
        data-testid={testid}
      />
      {error && (
        <div data-testid={`${testid}-error`} className="mt-0.5 text-[9px] text-destructive">
          Not saved — {error}
        </div>
      )}
    </div>
  )
}

function UpsertWhereStepForm({ step, onChange }: StepFormProps) {
  const params = (step as BasicStep).params
  const initialTarget = (params.target as string) ?? (params.collection as string) ?? ''
  const initialMatchExpr =
    (params.matchExpr as string) ?? (params.match as string) ?? (params.where as string) ?? ''
  const [target, setTarget] = useState(initialTarget)
  const [matchExpr, setMatchExpr] = useState(initialMatchExpr)
  // COR-5: computed once — a later emit adds the canonical keys to params,
  // which must not retract the seeding mid-edit.
  const [legacySeed] = useState(() => readLegacyUpsertPayload(params))
  // The parsed legacy payload rides along on the next commit (per key, so a
  // cleared field cancels its own migration without losing the other's).
  const [pendingMigration, setPendingMigration] = useState<
    Partial<Record<'onMatch' | 'onNew', Record<string, unknown>>>
  >(() =>
    legacySeed.object !== undefined
      ? { onMatch: legacySeed.object, onNew: legacySeed.object }
      : {})
  const [onMatchDraft, setOnMatchDraft] = useState(() =>
    params.onMatch != null
      ? JSON.stringify(params.onMatch, null, 2)
      : legacySeed.object !== undefined
        ? JSON.stringify(legacySeed.object, null, 2)
        : legacySeed.raw ?? '')
  const [onNewDraft, setOnNewDraft] = useState(() =>
    params.onNew != null
      ? JSON.stringify(params.onNew, null, 2)
      : legacySeed.object !== undefined
        ? JSON.stringify(legacySeed.object, null, 2)
        : legacySeed.raw ?? '')
  const [jsonErrors, setJsonErrors] = useState<Record<string, string>>({})

  // QA-4: legacy-NAMED steps reach dedicated forms through the alias
  // dispatcher and must obey the strict no-op-blur discipline (tab-through
  // never migrates). Canonically-named steps may heal on blur below.
  const isLegacyNamed = (step as BasicStep).action !== 'upsertWhere'
  // Steps persisted by the pre-F2 form carry match/where but never matchExpr;
  // the engine reads ONLY matchExpr, so the step LOOKS configured but
  // silently no-ops. Mirroring SetStateStepForm's alias-seeded relaxation,
  // blurring the untouched field commits the canonical shape (heal-on-blur).
  const matchSeededFromLegacy =
    params.matchExpr === undefined && initialMatchExpr !== '' && !isLegacyNamed

  // WRITE-CANONICAL ONLY: every commit drops all five legacy keys. COR-5: a
  // pending legacy data/value payload migration rides along on the SAME
  // commit (only while the canonical key is still absent from committed
  // params), so the payload moves into onMatch/onNew instead of being
  // dropped. COR-6: `removeKey` deletes a canonical key from the emit —
  // clearing a JSON field is the unset affordance.
  const emit = (patch: Record<string, unknown>, removeKey?: 'onMatch' | 'onNew') => {
    const next: Record<string, unknown> = {
      ...canonicalizeWhereParams(params, { target: ['collection'], matchExpr: ['match', 'where'] }),
      ...(pendingMigration.onMatch !== undefined && params.onMatch === undefined
        ? { onMatch: pendingMigration.onMatch }
        : {}),
      ...(pendingMigration.onNew !== undefined && params.onNew === undefined
        ? { onNew: pendingMigration.onNew }
        : {}),
      ...patch,
    }
    if (removeKey) delete next[removeKey]
    onChange(next)
  }

  const commitJson = (key: 'onMatch' | 'onNew', draft: string) => {
    const original = params[key]
    const clearError = () =>
      setJsonErrors((prev) => {
        if (!(key in prev)) return prev
        const next = { ...prev }
        delete next[key]
        return next
      })
    if (draft.trim() === '') {
      // COR-6: an emptied field is the unset affordance — REMOVE the param.
      // The engine keys update-only/insert-only semantics off the key's
      // ABSENCE (committing {} would instead merge/append empty rows), so
      // there must be a way out of a committed onMatch/onNew. Clearing a
      // migration-seeded draft cancels that key's pending migration the same
      // way; an untouched empty field stays a no-op.
      clearError()
      if (pendingMigration[key] !== undefined) {
        setPendingMigration((prev) => ({ ...prev, [key]: undefined }))
      }
      if (original !== undefined) emit({}, key)
      return
    }
    let parsed: unknown
    try {
      parsed = JSON.parse(draft)
    } catch (err) {
      setJsonErrors((prev) => ({ ...prev, [key]: (err as Error).message }))
      return
    }
    // The engine expects a column-value dictionary — committing a scalar or
    // array would silently misbehave at runtime.
    if (parsed == null || typeof parsed !== 'object' || Array.isArray(parsed)) {
      setJsonErrors((prev) => ({ ...prev, [key]: 'expected a JSON object' }))
      return
    }
    clearError()
    if (original !== undefined && JSON.stringify(parsed) === JSON.stringify(original)) return
    // QA-4: legacy-NAMED steps obey the strict no-op-blur discipline (tab-
    // through never migrates) — an untouched blur of a migration-seeded
    // draft must not commit. Canonically-named steps DO heal on blur,
    // mirroring the matchExpr relaxation above.
    if (
      original === undefined &&
      isLegacyNamed &&
      JSON.stringify(parsed) === JSON.stringify(legacySeed.object)
    ) {
      return
    }
    emit({ [key]: parsed })
  }

  return (
    <div className="space-y-2">
      <BareTargetField
        initialTarget={initialTarget}
        target={target}
        setTarget={setTarget}
        onCommit={() => emit({ target })}
        testid="param-input-target"
        errorTestid="upsertwhere-target-error"
        placeholder="appData.cart.items"
      />
      <div>
        <label className={FORM.labelClass}>Match Expression <span className="text-rose-400">*</span></label>
        <VariableAutocomplete
          mode="expression"
          value={matchExpr}
          onChange={(v) => setMatchExpr(v)}
          onBlur={() => {
            if (matchExpr === initialMatchExpr && !matchSeededFromLegacy) return
            emit({ matchExpr })
          }}
          placeholder="item => item.id === '#{local.productId}'"
          testid="param-input-matchExpr"
        />
      </div>
      <JsonObjectField
        label="On Match"
        draft={onMatchDraft}
        error={jsonErrors.onMatch}
        testid="param-json-onMatch"
        onDraftChange={(v) => setOnMatchDraft(v)}
        onCommit={() => commitJson('onMatch', onMatchDraft)}
        placeholder='{ "qty": "#{item.qty + 1}" }'
      />
      <JsonObjectField
        label="On New"
        draft={onNewDraft}
        error={jsonErrors.onNew}
        testid="param-json-onNew"
        onDraftChange={(v) => setOnNewDraft(v)}
        onCommit={() => commitJson('onNew', onNewDraft)}
        placeholder='{ "id": "#{event.id}", "qty": 1 }'
      />
    </div>
  )
}

function RemoveWhereStepForm({ step, onChange }: StepFormProps) {
  const params = (step as BasicStep).params
  const initialTarget = (params.target as string) ?? (params.collection as string) ?? ''
  const initialPredicate =
    (params.predicate as string) ?? (params.match as string) ?? (params.where as string) ?? ''
  const [target, setTarget] = useState(initialTarget)
  const [predicate, setPredicate] = useState(initialPredicate)

  const isLegacyNamed = (step as BasicStep).action !== 'removeWhere'
  // Pre-F2 steps carry match/where but never predicate — the engine silently
  // skips when params.predicate is absent. Heal-on-blur (see UpsertWhere).
  const predicateSeededFromLegacy =
    params.predicate === undefined && initialPredicate !== '' && !isLegacyNamed

  // WRITE-CANONICAL ONLY: every commit drops all five legacy keys.
  const emit = (patch: Record<string, unknown>) => {
    onChange({
      ...canonicalizeWhereParams(params, { target: ['collection'], predicate: ['match', 'where'] }),
      ...patch,
    })
  }

  return (
    <div className="space-y-2">
      <BareTargetField
        initialTarget={initialTarget}
        target={target}
        setTarget={setTarget}
        onCommit={() => emit({ target })}
        testid="param-input-target"
        errorTestid="removewhere-target-error"
        placeholder="appData.cart.items"
      />
      <div>
        <label className={FORM.labelClass}>Predicate <span className="text-rose-400">*</span></label>
        <VariableAutocomplete
          mode="expression"
          value={predicate}
          onChange={(v) => setPredicate(v)}
          onBlur={() => {
            if (predicate === initialPredicate && !predicateSeededFromLegacy) return
            emit({ predicate })
          }}
          placeholder="item => item.id === '#{local.productId}'"
          testid="param-input-predicate"
        />
      </div>
    </div>
  )
}

function ExpressionStepForm({ step, onChange }: StepFormProps) {
  const params = (step as BasicStep).params
  const initialTarget = (params.target as string) ?? ''
  const [target, setTarget] = useState(initialTarget)
  const [expression, setExpression] = useState((params.expression as string) ?? (params.value as string) ?? '')

  const emit = (patch: Record<string, unknown>) => {
    onChange({ ...params, ...patch })
  }

  return (
    <div className="space-y-2">
      <BareTargetField
        initialTarget={initialTarget}
        target={target}
        setTarget={setTarget}
        onCommit={() => emit({ target })}
        testid="param-input-target"
        errorTestid="expression-target-error"
      />
      <div>
        <label className={FORM.labelClass}>Expression <span className="text-rose-400">*</span></label>
        <VariableAutocomplete
          mode="expression"
          value={expression}
          onChange={(v) => setExpression(v)}
          onBlur={() => emit({ expression, value: expression })}
          placeholder="#{local.price} * #{local.qty}"
          testid="param-input-expression"
        />
      </div>
    </div>
  )
}

function CallFlowStepForm({ step, onChange }: StepFormProps) {
  const params = (step as BasicStep).params
  const flows = useFlowEditorStore((s) => s.flows)
  const flowNames = useMemo(
    () => [...new Set(flows.map((f) => f.name))],
    [flows],
  )
  const initial = (params.flowName as string) ?? (params.flow as string) ?? (params.target as string) ?? ''
  const [flowName, setFlowName] = useState(initial)
  // Manual entry stays available as an escape hatch — flows defined outside
  // the currently loaded project (or not yet created) have no dropdown entry.
  const [manual, setManual] = useState(() => initial !== '' && !flowNames.includes(initial))

  const emit = (name: string) => {
    // flowName/flow/target are runtime-accepted aliases — keep all three in
    // sync so older flows that read a different alias stay consistent.
    onChange({ ...params, flowName: name, flow: name, target: name })
  }

  return (
    <div className="space-y-2">
      <div>
        <label className={FORM.labelClass}>Flow Name <span className="text-rose-400">*</span></label>
        {manual || flowNames.length === 0 ? (
          <VariableAutocomplete
            mode="expression"
            value={flowName}
            onChange={(v) => setFlowName(v)}
            onBlur={() => { if (flowName !== initial) emit(flowName) }}
            placeholder="onLoadProduct"
            testid="param-input-flowName"
          />
        ) : (
          <select
            className="w-full mt-0.5 text-xs border border-border rounded px-2 py-1 bg-background"
            value={flowNames.includes(flowName) ? flowName : ''}
            onChange={(e) => { setFlowName(e.target.value); emit(e.target.value) }}
            data-testid="param-select-flowName"
          >
            <option value="" disabled>Select a flow...</option>
            {flowNames.map((name) => (
              <option key={name} value={name}>{name}</option>
            ))}
          </select>
        )}
      </div>
      {flowNames.length > 0 && (
        <button
          type="button"
          data-testid="callflow-manual-toggle"
          className="text-[10px] text-muted-foreground hover:text-foreground underline-offset-2 hover:underline transition-colors"
          onClick={() => setManual((m) => !m)}
        >
          {manual ? 'Pick from existing flows' : 'Enter flow name manually'}
        </button>
      )}
    </div>
  )
}

function RestApiStepForm({ step, onChange }: StepFormProps) {
  const params = (step as BasicStep).params
  const initialUrl = (params.url as string) ?? ''
  // Object bodies (the entire wild restful_data_action population carries
  // body as an object) display as JSON — the previous string cast rendered
  // them as "[object Object]" in the textarea, and editing THAT field
  // committed the corrupted string.
  const initialBodyValue: unknown = params.body ?? ''
  const initialBodyText =
    typeof initialBodyValue === 'object' && initialBodyValue !== null
      ? JSON.stringify(initialBodyValue, null, 2)
      : String(initialBodyValue)
  const initialResultTarget = (params.resultTarget as string) ?? (params.responseKey as string) ?? ''
  const [url, setUrl] = useState(initialUrl)
  const [method, setMethod] = useState((params.method as string) ?? 'GET')
  const [bodyText, setBodyText] = useState(initialBodyText)
  // The COMMITTED body value — an untouched object body must survive every
  // OTHER field's commit verbatim (emit must never substitute display text).
  const [bodyValue, setBodyValue] = useState<unknown>(initialBodyValue)
  const [resultTarget, setResultTarget] = useState(initialResultTarget)

  const emit = (patch: Record<string, unknown>) => {
    onChange({ ...params, url, method, body: bodyValue, resultTarget, ...patch })
  }

  const commitBody = () => {
    // No-op blur emits NOTHING (#969/#970 discipline; QA-4 aliased path).
    if (bodyText === initialBodyText) return
    // Text that parses commits typed (objects/arrays round-trip instead of
    // double-encoding at the runtime's JSON.stringify); anything else
    // commits as the raw string.
    let parsed: unknown = bodyText
    try {
      parsed = JSON.parse(bodyText)
    } catch {
      parsed = bodyText
    }
    setBodyValue(parsed)
    emit({ body: parsed })
  }

  return (
    <div className="space-y-2">
      <div>
        <label className={FORM.labelClass}>Method</label>
        <select
          className="w-full mt-0.5 text-xs border border-border rounded px-2 py-1 bg-background"
          value={method}
          onChange={(e) => { setMethod(e.target.value); emit({ method: e.target.value }) }}
        >
          {['GET', 'POST', 'PUT', 'PATCH', 'DELETE'].map((m) => (
            <option key={m} value={m}>{m}</option>
          ))}
        </select>
      </div>
      <div>
        <label className={FORM.labelClass}>URL <span className="text-rose-400">*</span></label>
        <VariableAutocomplete
          mode="expression"
          value={url}
          onChange={(v) => setUrl(v)}
          // No-op blur emits NOTHING (#969/#970 discipline; QA-4 aliased path).
          onBlur={() => { if (url !== initialUrl) emit({ url }) }}
          placeholder="https://api.example.com/data"
          testid="param-input-url"
        />
      </div>
      {method !== 'GET' && method !== 'DELETE' && (
        <div>
          <label className={FORM.labelClass}>Body (JSON)</label>
          <textarea
            className="w-full mt-0.5 text-xs border border-border rounded px-2 py-1 bg-background font-mono min-h-[60px] resize-y"
            value={bodyText}
            onChange={(e) => setBodyText(e.target.value)}
            onBlur={commitBody}
            placeholder='{ "key": "value" }'
            data-testid="param-input-body"
          />
        </div>
      )}
      <div>
        <label className={FORM.labelClass}>Result Target</label>
        <VariableAutocomplete
          mode="expression"
          value={resultTarget}
          onChange={(v) => setResultTarget(v)}
          onBlur={() => { if (resultTarget !== initialResultTarget) emit({ resultTarget }) }}
          placeholder="_results.response"
          testid="param-input-resultTarget"
        />
      </div>
    </div>
  )
}

function DomainOperationStepForm({ step, onFieldsChange }: StepFormProps) {
  const record = step as unknown as Record<string, unknown>
  const initialTarget = typeof record.target === 'string' ? record.target : ''
  const initialSourceExpression = typeof record.sourceExpression === 'string' ? record.sourceExpression : ''
  const initialTool = typeof record.tool === 'string' ? record.tool : ''
  const initialOperation = typeof record.operation === 'string' ? record.operation : ''
  const initialIntent = typeof record.intent === 'string' ? record.intent : ''
  const initialQuery = typeof record.query === 'string' ? record.query : ''

  const [target, setTarget] = useState(initialTarget)
  const [sourceExpression, setSourceExpression] = useState(initialSourceExpression)
  const [tool, setTool] = useState(initialTool)
  const [operation, setOperation] = useState(initialOperation)
  const [intent, setIntent] = useState(initialIntent)
  const [query, setQuery] = useState(initialQuery)

  const commitRequired = (key: string, value: string, initial: string) => {
    const next = value.trim()
    if (!next || next === initial) return
    onFieldsChange?.({ [key]: next })
  }
  const commitOptional = (key: string, value: string, initial: string) => {
    if (value === initial) return
    onFieldsChange?.({ [key]: value })
  }

  return (
    <div className="space-y-2">
      <div className="rounded-md border border-violet-500/25 bg-violet-500/5 px-2.5 py-2 text-[10px] text-muted-foreground">
        <div className="font-medium text-foreground">WebMCP/domain operation</div>
        <div className="mt-0.5">
          Edits keep the source-derived <span className="font-mono">domain-operation</span> step shape and are safe to dry-run with side effects suppressed.
        </div>
      </div>
      <FormField
        label="Target"
        required
        value={target}
        onChange={setTarget}
        onBlur={() => commitRequired('target', target, initialTarget)}
        placeholder="Daily operations summary"
        testid="field-input-domain-target"
      />
      <div>
        <label className={FORM.labelClass}>Source Expression</label>
        <textarea
          className="w-full mt-0.5 text-xs border border-border rounded px-2 py-1 bg-background font-mono min-h-[70px] resize-y"
          value={sourceExpression}
          onChange={(e) => setSourceExpression(e.target.value)}
          onBlur={() => commitOptional('sourceExpression', sourceExpression, initialSourceExpression)}
          placeholder="Source code intent or WebMCP tool description"
          data-testid="field-input-domain-sourceExpression"
        />
      </div>
      <div className="grid grid-cols-2 gap-2">
        <FormField
          label="Tool"
          value={tool}
          onChange={setTool}
          onBlur={() => commitOptional('tool', tool, initialTool)}
          placeholder="useAdminWebMCP"
          testid="field-input-domain-tool"
        />
        <FormField
          label="Operation"
          value={operation}
          onChange={setOperation}
          onBlur={() => commitOptional('operation', operation, initialOperation)}
          placeholder="whoami"
          testid="field-input-domain-operation"
        />
      </div>
      <FormField
        label="Intent"
        value={intent}
        onChange={setIntent}
        onBlur={() => commitOptional('intent', intent, initialIntent)}
        placeholder="Natural-language admin intent"
        testid="field-input-domain-intent"
      />
      <FormField
        label="Query"
        value={query}
        onChange={setQuery}
        onBlur={() => commitOptional('query', query, initialQuery)}
        placeholder="Optional command/query payload"
        testid="field-input-domain-query"
      />
    </div>
  )
}

function SourceExpressionField({
  value,
  onChange,
  onBlur,
  testid,
  placeholder,
}: {
  value: string
  onChange: (value: string) => void
  onBlur: () => void
  testid: string
  placeholder: string
}) {
  return (
    <div>
      <label className={FORM.labelClass}>Source Expression</label>
      <textarea
        className="w-full mt-0.5 text-xs border border-border rounded px-2 py-1 bg-background font-mono min-h-[70px] resize-y"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onBlur={onBlur}
        placeholder={placeholder}
        data-testid={testid}
      />
    </div>
  )
}

function SourceTypedActionStepForm({ step, onFieldsChange }: StepFormProps) {
  const record = step as unknown as Record<string, unknown>
  const type = typeof record.type === 'string' ? record.type : 'event'
  const initialTarget = typeof record.target === 'string' ? record.target : ''
  const initialValue = typeof record.value === 'string' ? record.value : ''
  const initialSourceExpression = typeof record.sourceExpression === 'string' ? record.sourceExpression : ''

  const [target, setTarget] = useState(initialTarget)
  const [value, setValue] = useState(initialValue)
  const [sourceExpression, setSourceExpression] = useState(initialSourceExpression)

  const commitRequired = (key: string, nextValue: string, initial: string) => {
    const next = nextValue.trim()
    if (!next || next === initial) return
    onFieldsChange?.({ [key]: next })
  }
  const commitOptional = (key: string, nextValue: string, initial: string) => {
    if (nextValue === initial) return
    onFieldsChange?.({ [key]: nextValue })
  }

  const label = type === 'setState'
    ? 'Source setState action'
    : type === 'navigate'
      ? 'Source navigation action'
      : type === 'callback'
        ? 'Source callback action'
        : 'Source event action'

  return (
    <div className="space-y-2">
      <div className="rounded-md border border-amber-500/25 bg-amber-500/5 px-2.5 py-2 text-[10px] text-muted-foreground">
        <div className="font-medium text-foreground">{label}</div>
        <div className="mt-0.5">
          Edits keep the source-derived <span className="font-mono">{type}</span> top-level step shape.
        </div>
      </div>
      {type === 'navigate' && (
        <FormField
          label="Target route"
          required
          value={target}
          onChange={setTarget}
          onBlur={() => commitRequired('target', target, initialTarget)}
          placeholder="/bookings"
          testid="field-input-source-navigate-target"
        />
      )}
      {type === 'setState' && (
        <div className="grid grid-cols-2 gap-2">
          <FormField
            label="Target"
            value={target}
            onChange={setTarget}
            onBlur={() => commitOptional('target', target, initialTarget)}
            placeholder="value"
            testid="field-input-source-setState-target"
          />
          <FormField
            label="Value"
            value={value}
            onChange={setValue}
            onBlur={() => commitOptional('value', value, initialValue)}
            placeholder="#{event.target.value}"
            testid="field-input-source-setState-value"
          />
        </div>
      )}
      <SourceExpressionField
        value={sourceExpression}
        onChange={setSourceExpression}
        onBlur={() => commitOptional('sourceExpression', sourceExpression, initialSourceExpression)}
        placeholder="Source handler or expression"
        testid={`field-input-source-${type}-sourceExpression`}
      />
    </div>
  )
}

function ConditionalStepForm({ step, onFieldsChange }: StepFormProps) {
  const cond = step as ConditionalStep
  const [condition, setCondition] = useState(cond.condition ?? '')

  return (
    <div className="space-y-2">
      <div>
        <label className={FORM.labelClass}>
          Condition <span className="text-rose-400">*</span>
        </label>
        <VariableAutocomplete
          mode="expression"
          value={condition}
          onChange={(v) => setCondition(v)}
          // Guard against the seed fallback: when the persisted step omits the
          // field (schema-violating LLM output), a no-typing blur must not
          // commit the '' seed — no-op blur emits nothing for every data class.
          onBlur={() => { if (condition !== (cond.condition ?? '')) onFieldsChange?.({ condition }) }}
          placeholder="#{local.isValid}"
          testid="field-input-condition"
        />
      </div>
      <div className="text-[10px] text-muted-foreground">
        {cond.then.length} then step{cond.then.length !== 1 ? 's' : ''}
        {cond.else ? `, ${cond.else.length} else step${cond.else.length !== 1 ? 's' : ''}` : ''}
      </div>
    </div>
  )
}

function LoopStepForm({ step, onFieldsChange }: StepFormProps) {
  const loop = step as LoopStep
  const [collection, setCollection] = useState(loop.collection ?? '')
  const [itemVar, setItemVar] = useState(loop.itemVar ?? 'item')

  return (
    <div className="space-y-2">
      <div>
        <label className={FORM.labelClass}>Collection <span className="text-rose-400">*</span></label>
        <VariableAutocomplete
          mode="expression"
          value={collection}
          onChange={(v) => setCollection(v)}
          // Changed-vs-step guard: a no-typing blur on a step that omits the
          // field must not commit the '' seed (see ConditionalStepForm).
          onBlur={() => { if (collection !== (loop.collection ?? '')) onFieldsChange?.({ collection }) }}
          placeholder="#{appData.items}"
          testid="field-input-collection"
        />
      </div>
      <div>
        <label className={FORM.labelClass}>Item Variable</label>
        <input
          className="w-full mt-0.5 text-xs border border-border rounded px-2 py-1 bg-background font-mono"
          value={itemVar}
          onChange={(e) => setItemVar(e.target.value)}
          onBlur={() => {
            const name = itemVar.trim()
            if (!name) {
              // Rejected commit — restore the stored name so the input never
              // displays blank over a non-blank persisted value.
              setItemVar(loop.itemVar ?? 'item')
              return
            }
            // Changed-vs-step guard: blurring the 'item' seed on a step that
            // omits itemVar must not commit the fallback (no-op blur).
            if (name !== (loop.itemVar ?? 'item')) onFieldsChange?.({ itemVar: name })
          }}
          placeholder="item"
          data-testid="field-input-itemVar"
        />
        <div className="mt-0.5 text-[9px] text-muted-foreground/70">
          Bare name, no <span className="font-mono">#{'{}'}</span>. Loop-body steps read the
          current element as <span className="font-mono">#{'{item.<field>}'}</span> — renaming
          to e.g. <span className="font-mono">task</span> makes it{' '}
          <span className="font-mono">#{'{task.<field>}'}</span>.
        </div>
      </div>
      <div className="text-[10px] text-muted-foreground">
        {loop.steps.length} body step{loop.steps.length !== 1 ? 's' : ''}
      </div>
    </div>
  )
}

/** One switch-case row with an inline-renamable key. Keyed by caseKey at the
 *  call site, so a successful rename remounts the row with the new name; an
 *  unchanged or store-refused commit (#969 contract: dirty only on real
 *  change — blank / duplicate / same key are store no-ops) resets the draft
 *  back to the live key. */
function SwitchCaseRow({
  caseKey,
  stepCount,
  onRenameCase,
  onRemoveCase,
}: {
  caseKey: string
  stepCount: number
  onRenameCase?: (oldKey: string, newKey: string) => void
  onRemoveCase?: (caseKey: string) => void
}) {
  const [draft, setDraft] = useState(caseKey)
  const commit = () => {
    const next = draft.trim()
    if (!next || next === caseKey) {
      setDraft(caseKey)
      return
    }
    onRenameCase?.(caseKey, next)
    // If the store refused (duplicate key), the prop is unchanged and the
    // draft must snap back; on success the row remounts under the new key.
    setDraft(caseKey)
  }
  return (
    <div className="flex items-center gap-1.5 rounded border border-border bg-muted/30 px-2 py-1">
      <input
        className="flex-1 min-w-0 truncate text-[11px] font-mono bg-transparent border-none p-0 focus:outline-none focus:ring-1 focus:ring-ring rounded-sm"
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => {
          if (e.key === 'Enter') (e.target as HTMLInputElement).blur()
          if (e.key === 'Escape') setDraft(caseKey)
        }}
        title={`Rename case ${caseKey}`}
        data-testid={`switch-case-rename-${caseKey}`}
      />
      <span className="shrink-0 text-[9px] text-muted-foreground">
        {stepCount} step{stepCount !== 1 ? 's' : ''}
      </span>
      <button
        type="button"
        data-testid={`switch-case-remove-${caseKey}`}
        title={`Remove case ${caseKey}`}
        className="shrink-0 p-0.5 text-muted-foreground hover:text-destructive rounded transition-colors"
        onClick={() => onRemoveCase?.(caseKey)}
      >
        <X size={10} />
      </button>
    </div>
  )
}

function SwitchStepForm({ step, onFieldsChange, onAddCase, onRenameCase, onRemoveCase }: StepFormProps) {
  const sw = step as SwitchStep
  const [value, setValue] = useState(sw.value ?? '')
  const [newCaseKey, setNewCaseKey] = useState('')
  const cases = Object.entries(sw.cases ?? {})

  const addCase = () => {
    const key = newCaseKey.trim()
    if (!key) return
    onAddCase?.(key)
    setNewCaseKey('')
  }

  return (
    <div className="space-y-2">
      <div>
        <label className={FORM.labelClass}>Switch Value <span className="text-rose-400">*</span></label>
        <VariableAutocomplete
          mode="expression"
          value={value}
          onChange={(v) => setValue(v)}
          // Changed-vs-step guard: a no-typing blur on a step that omits the
          // field must not commit the '' seed (see ConditionalStepForm).
          onBlur={() => { if (value !== (sw.value ?? '')) onFieldsChange?.({ value }) }}
          placeholder="#{appData.status}"
          testid="field-input-value"
        />
      </div>
      <div>
        <label className={FORM.labelClass}>Cases</label>
        {cases.length === 0 && (
          <div className="mt-0.5 text-[10px] text-muted-foreground/70">No cases yet.</div>
        )}
        <div className="mt-0.5 space-y-1">
          {cases.map(([caseKey, caseSteps]) => (
            <SwitchCaseRow
              key={caseKey}
              caseKey={caseKey}
              stepCount={caseSteps.length}
              onRenameCase={onRenameCase}
              onRemoveCase={onRemoveCase}
            />
          ))}
        </div>
        <div className="mt-1 flex items-center gap-1">
          <input
            className="flex-1 min-w-0 text-xs border border-border rounded px-2 py-1 bg-background font-mono"
            value={newCaseKey}
            onChange={(e) => setNewCaseKey(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') addCase() }}
            placeholder="case value (e.g. gold)"
            data-testid="switch-case-key-input"
          />
          <button
            type="button"
            data-testid="switch-case-add"
            className="shrink-0 flex items-center gap-0.5 text-[10px] rounded border border-border px-1.5 py-1 text-muted-foreground hover:bg-accent hover:text-foreground transition-colors"
            onClick={addCase}
          >
            <Plus size={10} /> Add
          </button>
        </div>
      </div>
      <div className="text-[10px] text-muted-foreground">
        {sw.default ? `${sw.default.length} default step${sw.default.length !== 1 ? 's' : ''}` : 'No default branch'}
      </div>
    </div>
  )
}

function TryCatchStepForm({ step }: StepFormProps) {
  const tc = step as TryCatchStep
  return (
    <div className="text-[10px] text-muted-foreground space-y-1">
      <div>{tc.try.length} try step{tc.try.length !== 1 ? 's' : ''}</div>
      <div>{tc.catch?.length ?? 0} catch step{tc.catch?.length !== 1 ? 's' : ''}</div>
      <div>{tc.finally?.length ?? 0} finally step{tc.finally?.length !== 1 ? 's' : ''}</div>
      <div className="italic">Nested steps are shown in the center panel.</div>
    </div>
  )
}

function ParallelStepForm({ step }: StepFormProps) {
  const par = step as ParallelStep
  return (
    <div className="text-[10px] text-muted-foreground space-y-1">
      <div>{par.steps.length} parallel step{par.steps.length !== 1 ? 's' : ''} — run concurrently</div>
      <div className="italic">Nested steps are shown in the center panel.</div>
    </div>
  )
}

function StageStepForm({ step, onFieldsChange }: StepFormProps) {
  const stage = step as StageStep
  const [name, setName] = useState(stage.name ?? '')
  const failurePolicy = stage.failurePolicy ?? 'stop'
  return (
    <div className="space-y-2">
      <div>
        <label className={FORM.labelClass}>Stage Name <span className="text-rose-400">*</span></label>
        <input
          className="w-full mt-0.5 text-xs border border-border rounded px-2 py-1 bg-background font-mono"
          value={name}
          onChange={(event) => setName(event.target.value)}
          onBlur={() => {
            const next = name.trim()
            if (!next) {
              setName(stage.name)
              return
            }
            if (next !== stage.name) onFieldsChange?.({ name: next })
          }}
          data-testid="field-input-stage-name"
        />
      </div>
      <div>
        <label className={FORM.labelClass}>Failure Policy</label>
        <select
          className="w-full mt-0.5 text-xs border border-border rounded px-2 py-1 bg-background"
          value={failurePolicy}
          onChange={(event) => onFieldsChange?.({ failurePolicy: event.target.value })}
          data-testid="field-input-stage-failure-policy"
        >
          <option value="stop">Stop and propagate</option>
          <option value="continue" disabled={!stage.onError?.length}>Continue after onError</option>
        </select>
        {!stage.onError?.length && (
          <div className="mt-0.5 text-[9px] text-muted-foreground/70">
            Add an on-error step before selecting continue.
          </div>
        )}
      </div>
      <div className="text-[10px] text-muted-foreground space-y-0.5">
        <div>{stage.steps.length} body step{stage.steps.length !== 1 ? 's' : ''}</div>
        <div>{stage.onEnter?.length ?? 0} on-enter · {stage.onSuccess?.length ?? 0} on-success</div>
        <div>{stage.onError?.length ?? 0} on-error · {stage.onFinally?.length ?? 0} on-finally</div>
        <div>{stage.compensate?.length ?? 0} compensation step{stage.compensate?.length !== 1 ? 's' : ''}</div>
      </div>
    </div>
  )
}

function SagaStepForm({ step, onFieldsChange }: StepFormProps) {
  const saga = step as SagaStep
  return (
    <div className="space-y-2">
      <div>
        <label className={FORM.labelClass}>Compensation Failure</label>
        <select
          className="w-full mt-0.5 text-xs border border-border rounded px-2 py-1 bg-background"
          value={saga.compensationFailurePolicy ?? 'continue'}
          onChange={(event) => onFieldsChange?.({ compensationFailurePolicy: event.target.value })}
          data-testid="field-input-saga-compensation-policy"
        >
          <option value="continue">Continue older compensations</option>
          <option value="stop">Stop at first compensation failure</option>
        </select>
      </div>
      <div className="text-[10px] text-muted-foreground">
        {saga.stages.length} ordered stage{saga.stages.length !== 1 ? 's' : ''}. Completed stages compensate in reverse order.
      </div>
      <div className="text-[9px] text-muted-foreground/70 italic">
        Saga compensation is an execution policy, not an ACID database rollback.
      </div>
    </div>
  )
}

/** Hints for action types that may have empty params from the compiler */
const EXPECTED_PARAMS: Record<string, { label: string; fields: string[] }> = {
  // F2: runtime-canonical names — the engine alias layer reads ONLY these
  // (the old collection/match/data hints repeated the broken form vocabulary).
  upsertWhere: { label: 'Upsert Where', fields: [...UPSERT_WHERE_FORM_PARAMS] },
  removeWhere: { label: 'Remove Where', fields: [...REMOVE_WHERE_FORM_PARAMS] },
  updateWhere: { label: 'Update Where', fields: [...UPDATE_WHERE_FORM_PARAMS] },
  setState: { label: 'Set State', fields: ['target', 'value'] },
  compute: { label: 'Compute', fields: ['target', 'expression'] },
  expression: { label: 'Expression', fields: ['target', 'expression'] },
  navigate: { label: 'Navigate', fields: ['route'] },
  callFlow: { label: 'Call Flow', fields: ['flowName'] },
  restApi: { label: 'REST API', fields: ['method', 'url', 'body', 'resultTarget'] },
  restful_data_action: { label: 'REST API', fields: ['method', 'url', 'body', 'resultTarget'] },
  showToast: { label: 'Show Toast', fields: ['message', 'variant'] },
  scrollToPosition: { label: 'Scroll To', fields: ['target', 'position'] },
  graphql_action: { label: 'GraphQL', fields: ['query', 'variables', 'resultTarget'] },
  supabase_select: { label: 'Supabase Select', fields: ['table', 'columns', 'filter', 'resultTarget'] },
  supabase_insert: { label: 'Supabase Insert', fields: ['table', 'data', 'resultTarget'] },
  supabase_update: { label: 'Supabase Update', fields: ['table', 'data', 'filter'] },
  supabase_delete: { label: 'Supabase Delete', fields: ['table', 'filter'] },
  show_popup_action: { label: 'Show Popup', fields: ['popupId'] },
  open_drawer_action: { label: 'Open Drawer', fields: ['drawerId'] },
}

/** Only primitive params are editable as text. Objects/arrays render
 * read-only — String()-coercing them wrote `"[object Object]"` back into
 * the step (and the flowSteps auto-save persisted the corruption). */
function isPrimitiveParam(value: unknown): boolean {
  return value == null || typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean'
}

type ParamRowKind = 'primitive' | 'json'

function rowKindForDef(def: ActionParamDef | undefined): ParamRowKind {
  return def?.type === 'object' || def?.type === 'array' ? 'json' : 'primitive'
}

function GenericStepForm({ step, onChange }: StepFormProps) {
  const params = ('params' in step ? (step as BasicStep).params : {}) as Record<string, unknown>
  const actionName = 'action' in step ? (step as BasicStep).action : undefined
  const registryParams = actionName ? ACTION_DEFS[actionName]?.params : undefined

  const [values, setValues] = useState<Record<string, string>>(() =>
    Object.fromEntries(
      Object.entries(params).filter(([, v]) => isPrimitiveParam(v)).map(([k, v]) => [k, String(v ?? '')]),
    ),
  )
  // Object/array params are edited as JSON text; the draft only commits when
  // it parses — the typed value keeps its last good shape otherwise (F37/F46:
  // the old form String()-coerced these to "[object Object]" on any blur).
  const [jsonDrafts, setJsonDrafts] = useState<Record<string, string>>(() =>
    Object.fromEntries(
      Object.entries(params).filter(([, v]) => !isPrimitiveParam(v)).map(([k, v]) => [k, JSON.stringify(v, null, 2)]),
    ),
  )
  const [jsonErrors, setJsonErrors] = useState<Record<string, string>>({})
  // Rows added this session that are not yet committed params — adding a row
  // and tabbing away persists nothing.
  const [extraKeys, setExtraKeys] = useState<string[]>([])
  const [freeKey, setFreeKey] = useState('')

  const paramDefFor = (key: string) => registryParams?.find((p) => p.name === key)

  const emitPrimitive = (key: string, val: string) => {
    const original = params[key]
    // Preserve primitive types: a number/boolean param edited as text
    // round-trips back to its original type while the text still parses.
    let parsed: unknown = val
    if (typeof original === 'number') {
      const n = Number(val)
      parsed = val.trim() !== '' && Number.isFinite(n) ? n : val
    } else if (typeof original === 'boolean') {
      parsed = val === 'true' ? true : val === 'false' ? false : val
    }
    // No-op blur emits NOTHING — an untouched field (or one typed back to its
    // original value) must not rewrite params or arm the auto-save bridge.
    if (Object.is(parsed, original)) return
    if (original == null && val === '') return
    // Patch ONLY the edited key — every other param keeps its original
    // (typed) value. updateStep replaces the params map wholesale, so the
    // untouched keys must come from `params`, not from the string shadow.
    onChange({ ...params, [key]: parsed })
  }

  const commitJson = (key: string) => {
    const draft = jsonDrafts[key] ?? ''
    const original = params[key]
    if (draft.trim() === '' && original === undefined) return // untouched new row
    let parsed: unknown
    try {
      parsed = JSON.parse(draft)
    } catch (err) {
      setJsonErrors((prev) => ({ ...prev, [key]: (err as Error).message }))
      return
    }
    // A JSON row stays a JSON row: committing a scalar would flip the row to
    // a primitive input whose string shadow was never seeded — it would
    // display empty and the next no-typing blur would destructively write ''.
    if (isPrimitiveParam(parsed)) {
      setJsonErrors((prev) => ({ ...prev, [key]: 'expected a JSON object or array' }))
      return
    }
    setJsonErrors((prev) => {
      if (!(key in prev)) return prev
      const next = { ...prev }
      delete next[key]
      return next
    })
    if (original !== undefined && JSON.stringify(parsed) === JSON.stringify(original)) return
    onChange({ ...params, [key]: parsed })
  }

  // Adding a row never seeds a value — an added-then-untouched row must stay
  // uncommitted (blur of an empty new row emits nothing).
  const addRow = (rawKey: string) => {
    const key = rawKey.trim()
    if (!key || key in params || extraKeys.includes(key)) return
    setExtraKeys((prev) => [...prev, key])
  }

  const rows: Array<{ key: string; kind: ParamRowKind }> = [
    ...Object.entries(params).map(([k, v]) => ({
      key: k,
      kind: (isPrimitiveParam(v) ? 'primitive' : 'json') as ParamRowKind,
    })),
    ...extraKeys
      .filter((k) => !(k in params))
      .map((k) => ({ key: k, kind: rowKindForDef(paramDefFor(k)) })),
  ]

  // Missing-param suggestions: registry metadata first, legacy EXPECTED_PARAMS
  // hint names for actions the registry does not know.
  const present = new Set(rows.map((r) => r.key))
  const suggestions = (
    registryParams?.map((p) => p.name) ??
    (actionName ? EXPECTED_PARAMS[actionName]?.fields : undefined) ??
    []
  ).filter((name) => !present.has(name))

  return (
    <div className="space-y-2">
      {rows.length === 0 && (
        <div className="rounded-md border border-dashed border-border bg-muted/30 px-3 py-2.5">
          <div className="text-[11px] font-medium text-muted-foreground mb-1">
            Parameters not configured
          </div>
          {actionName && (
            <div className="text-[10px] text-muted-foreground/70">
              Action: <span className="font-mono">{actionName}</span>
            </div>
          )}
        </div>
      )}

      {rows.map(({ key, kind }) =>
        kind === 'primitive' ? (
          <div key={key}>
            <label className={FORM.labelClass}>{key}</label>
            <VariableAutocomplete
              mode="expression"
              value={values[key] ?? ''}
              onChange={(v) => setValues((prev) => ({ ...prev, [key]: v }))}
              onBlur={() => emitPrimitive(key, values[key] ?? '')}
              placeholder={key}
              testid={`param-input-${key}`}
            />
          </div>
        ) : (
          <div key={key}>
            <label className={FORM.labelClass}>
              {key} <span className="text-[9px] text-muted-foreground/70">(JSON)</span>
            </label>
            <textarea
              className="w-full mt-0.5 text-xs border border-border rounded px-2 py-1 bg-background font-mono min-h-[60px] resize-y"
              value={jsonDrafts[key] ?? ''}
              onChange={(e) => {
                const v = e.target.value
                setJsonDrafts((prev) => ({ ...prev, [key]: v }))
              }}
              onBlur={() => commitJson(key)}
              placeholder={paramDefFor(key)?.type === 'array' ? '[ ]' : '{ "key": "value" }'}
              data-testid={`param-json-${key}`}
            />
            {jsonErrors[key] && (
              <div data-testid={`param-json-error-${key}`} className="mt-0.5 text-[9px] text-destructive">
                Not saved — {jsonErrors[key]}
              </div>
            )}
          </div>
        ),
      )}

      {suggestions.length > 0 && (
        <div>
          <div className="text-[9px] text-muted-foreground/70 mb-0.5">Add parameter</div>
          <div className="flex flex-wrap gap-1">
            {suggestions.map((name) => (
              <button
                key={name}
                type="button"
                data-testid={`param-add-${name}`}
                title={paramDefFor(name)?.description}
                className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded bg-muted text-[9px] font-mono text-muted-foreground hover:bg-accent hover:text-foreground transition-colors"
                onClick={() => addRow(name)}
              >
                <Plus size={8} /> {name}
              </button>
            ))}
          </div>
        </div>
      )}

      <div className="flex items-center gap-1">
        <input
          className="flex-1 min-w-0 text-xs border border-border rounded px-2 py-1 bg-background font-mono"
          value={freeKey}
          onChange={(e) => setFreeKey(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') { addRow(freeKey); setFreeKey('') } }}
          placeholder="custom parameter name"
          data-testid="param-add-key-input"
        />
        <button
          type="button"
          data-testid="param-add-custom"
          className="shrink-0 flex items-center gap-0.5 text-[10px] rounded border border-border px-1.5 py-1 text-muted-foreground hover:bg-accent hover:text-foreground transition-colors"
          onClick={() => { addRow(freeKey); setFreeKey('') }}
        >
          <Plus size={10} /> Add
        </button>
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Reusable field component (kept for NavigateStepForm and ShowToastStepForm)
// ---------------------------------------------------------------------------

function FormField({
  label,
  required,
  value,
  onChange,
  onBlur,
  placeholder,
  testid,
}: {
  label: string
  required?: boolean
  value: string
  onChange: (v: string) => void
  onBlur?: () => void
  placeholder?: string
  testid?: string
}) {
  return (
    <div>
      <label className={FORM.labelClass}>
        {label}
        {required && <span className="text-rose-400 ml-0.5">*</span>}
      </label>
      <input
        className="w-full mt-0.5 text-xs border border-border rounded px-2 py-1 bg-background"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onBlur={onBlur}
        placeholder={placeholder}
        data-testid={testid}
      />
    </div>
  )
}

// ---------------------------------------------------------------------------
// Form dispatcher
// ---------------------------------------------------------------------------

// Canonical action names only — legacy names (set_state_action,
// restful_data_action, navigation_action, toast_action, ...) reach their
// dedicated form through resolveLegacyActionAlias in the dispatcher, which
// also rewires commits through onMigrate so an edit normalizes the step.
// Exported for the legacy-alias gate test (every resolver entry's canonical
// side must have a dedicated form here).
export const STEP_FORM_MAP: Record<string, React.FC<StepFormProps>> = {
  setState: SetStateStepForm,
  navigate: NavigateStepForm,
  compute: ComputeStepForm,
  expression: ExpressionStepForm,
  showToast: ShowToastStepForm,
  restApi: RestApiStepForm,
  upsertWhere: UpsertWhereStepForm,
  removeWhere: RemoveWhereStepForm,
  callFlow: CallFlowStepForm,
}

function StepFormDispatcher({ step, onChange, onFieldsChange, onAddCase, onRenameCase, onRemoveCase, onMigrate }: StepFormProps) {
  // Control-flow types have their own forms
  if (isDomainOperationStep(step)) {
    return <DomainOperationStepForm step={step} onChange={onChange} onFieldsChange={onFieldsChange} />
  }
  if (isSourceTypedActionStep(step)) {
    return <SourceTypedActionStepForm step={step} onChange={onChange} onFieldsChange={onFieldsChange} />
  }
  if (step.type === 'conditional') return <ConditionalStepForm step={step} onChange={onChange} onFieldsChange={onFieldsChange} />
  if (step.type === 'tryCatch') return <TryCatchStepForm step={step} onChange={onChange} />
  if (step.type === 'parallel') return <ParallelStepForm step={step} onChange={onChange} />
  if (step.type === 'stage') return <StageStepForm step={step} onChange={onChange} onFieldsChange={onFieldsChange} />
  if (step.type === 'saga') return <SagaStepForm step={step} onChange={onChange} onFieldsChange={onFieldsChange} />
  if (step.type === 'loop') return <LoopStepForm step={step} onChange={onChange} onFieldsChange={onFieldsChange} />
  if (step.type === 'switch') {
    return (
      <SwitchStepForm
        step={step}
        onChange={onChange}
        onFieldsChange={onFieldsChange}
        onAddCase={onAddCase}
        onRenameCase={onRenameCase}
        onRemoveCase={onRemoveCase}
      />
    )
  }

  // Basic/expression steps — dispatch by action name
  if ('action' in step) {
    const action = (step as BasicStep).action
    const Form = STEP_FORM_MAP[action]
    if (Form) return <Form step={step} onChange={onChange} />

    // QA-4: a legacy-named step opens the SAME dedicated form as its
    // canonical equivalent, seeded from the legacy params. Commits are
    // rewired through onMigrate so the FIRST real user edit atomically
    // normalizes both the action name and the param shape (edit-time
    // migration — single step, never bulk; tab-through emits nothing
    // because every form obeys the no-op-blur guard).
    const alias = resolveLegacyActionAlias(action)
    if (alias && onMigrate) {
      const CanonicalForm = STEP_FORM_MAP[alias.canonicalAction]
      if (CanonicalForm) {
        // Defensive: malformed legacy data may omit params entirely. The
        // dedicated forms read step.params unguarded (GenericStepForm
        // tolerated the omission) — never crash the alias path on it.
        const safeStep = (
          (step as BasicStep).params != null ? step : { ...step, params: {} }
        ) as FlowStep
        const migrateOnCommit = (params: Record<string, unknown>) =>
          onMigrate(alias.canonicalAction, alias.migrateParams(params))
        return <CanonicalForm step={safeStep} onChange={migrateOnCommit} />
      }
    }
  }

  // Fallback: generic key-value form
  return <GenericStepForm step={step} onChange={onChange} />
}

// ---------------------------------------------------------------------------
// StepInspector (main export)
// ---------------------------------------------------------------------------

export function StepInspector() {
  const selectedStepIndex = useFlowEditorStore((s) => s.selectedStepIndex)
  const selectedStepPath = useFlowEditorStore((s) => s.selectedStepPath)
  const setSelectedStepPath = useFlowEditorStore((s) => s.setSelectedStepPath)
  const selectedFlowId = useFlowEditorStore((s) => s.selectedFlowId)
  const flows = useFlowEditorStore((s) => s.flows)
  const project = useStudioStore((s) => s.currentProject)
  const blocks = (project?.blocks ?? {}) as Record<string, { flow?: Record<string, unknown> }>
  const nodes = useFlowEditorStore((s) => s.nodes)
  const flowSteps = useFlowEditorStore((s) => s.flowSteps)
  const flowStepsFlowId = useFlowEditorStore((s) => s.flowStepsFlowId)

  // Resolve selected flow name from editor flow list
  const flowName = useMemo(
    () => flows.find((f: { id: string }) => f.id === selectedFlowId)?.name ?? null,
    [flows, selectedFlowId],
  )

  // Find the flow definition from block data
  // Flow names may be namespaced (e.g. "store-nav.addToCart") — try both full and bare name
  const flowData = useMemo(() => {
    if (!flowName || Object.keys(blocks).length === 0) return null
    const result = findFlowOwner(blocks, flowName)
    if (result) return result
    const dotIdx = flowName.lastIndexOf('.')
    if (dotIdx > 0) {
      return findFlowOwner(blocks, flowName.slice(dotIdx + 1))
    }
    return null
  }, [flowName, blocks])

  // Resolve the steps array. STORE-FIRST (QA-2a): when the editing buffer is
  // seeded for the selected flow it is the SAME array FlowStepPanel renders,
  // so the panel and the inspector can never disagree — and a flow (or step)
  // that exists only in the editor, not yet persisted into any block, still
  // resolves. Flows not currently seeded fall back to the block-persisted
  // copy, then to canvas nodes. All inspector edits already write to the
  // store (updateStep/updateStepFields), so reading from it closes the loop.
  const steps: FlowStep[] = useMemo(() => {
    if (selectedFlowId != null && flowStepsFlowId === selectedFlowId) return flowSteps
    if (flowData) return flowData.flow.steps
    if (!nodes || nodes.length === 0) return []
    return nodes
      .filter((n) => n.nodeType !== 'trigger' && n.nodeType !== 'stickyNote')
      .map((n) => ({ type: 'basic' as const, action: n.actionType, params: n.parameters ?? {} }))
  }, [selectedFlowId, flowStepsFlowId, flowSteps, flowData, nodes])

  // Selected step — resolved via path when available, fallback to index
  const step = useMemo(() => {
    if (selectedStepPath != null) return resolveStepAtPath(steps, selectedStepPath)
    return selectedStepIndex != null ? steps[selectedStepIndex] ?? null : null
  }, [selectedStepPath, selectedStepIndex, steps])

  // --- Empty state ---
  if (step == null) {
    return (
      <div className="p-4 text-center text-xs text-muted-foreground">
        <MousePointerClick size={ICON.lg} className="mx-auto mb-2 opacity-50" />
        Select a step to inspect
      </div>
    )
  }

  const label = getStepLabel(step)
  const category = getStepCategory(step)
  const StepIcon = ACTION_ICONS[label] ?? ACTION_ICONS[step.type] ?? Play
  const categoryColor = CATEGORY_COLORS[category] ?? CATEGORY_COLORS.Control

  const currentPath: StepPath | null =
    selectedStepPath ?? (selectedStepIndex != null ? [selectedStepIndex] as [number] : null)
  const deleteDisabled = isProtectedSoleSagaStage(steps, currentPath)

  // All step-buffer mutations dispatch through the MCP bus so the undo
  // middleware records them (F4) — see flow-steps-dispatch.ts. The mutators
  // run synchronously inside the dispatch; selection updates stay local.
  const handleParamsChange = (params: Record<string, unknown>) => {
    if (currentPath == null) return
    dispatchFlowStepsOp('updateStep', [currentPath, params])
  }

  const handleFieldsChange = (patch: Record<string, unknown>) => {
    if (currentPath == null) return
    dispatchFlowStepsOp('updateStepFields', [currentPath, patch])
  }

  const handleMigrate = (canonicalAction: string, params: Record<string, unknown>) => {
    if (currentPath == null) return
    dispatchFlowStepsOp('migrateStepAction', [currentPath, canonicalAction, params])
  }

  // QA-4: legacy-named basic step — drives the inspector notice, the
  // runtime-skip chip, and the explicit migration affordance below.
  const stepAction = 'action' in step ? (step as BasicStep).action : undefined
  const legacyAlias = stepAction !== undefined ? resolveLegacyActionAlias(stepAction) : undefined
  const runtimeStatus = stepAction !== undefined ? legacyRuntimeStatus(stepAction) : undefined
  const legacySkipped = runtimeStatus === 'skipped'
  // F17: a deferred-skip name (e.g. show_toast_action, graphql_action,
  // supabase_*) silently skips on web exactly like a 'skipped' resolver key,
  // but has NO losslessly-derivable canonical form — so there is no migrate
  // target and it stays on GenericStepForm. It gets a STANDALONE honest skip
  // chip (NOT the legacy migrate notice, which needs canonicalAction +
  // migrateParams the deferred name lacks). legacyAlias is undefined for these,
  // so the two surfaces are mutually exclusive by construction.
  const deferredSkipped = runtimeStatus === 'deferred-skip'

  const handleAddCase = (caseKey: string) => {
    if (currentPath == null) return
    dispatchFlowStepsOp('addSwitchCase', [currentPath, caseKey])
  }

  const handleRenameCase = (oldKey: string, newKey: string) => {
    if (currentPath == null) return
    dispatchFlowStepsOp('renameSwitchCase', [currentPath, oldKey, newKey])
  }

  const handleRemoveCase = (caseKey: string) => {
    if (currentPath == null) return
    // Blast-radius feedback (F4): count the case body from the rendered step
    // BEFORE the mutator drops it. No blocking confirm — the delete is now
    // genuinely undoable, so the toast carries the platform-aware undo hint
    // instead (RUX-07: 'Cmd+Z' is a dead combo on Windows/Linux).
    const caseSteps = step.type === 'switch' ? getNestedSteps(step, `case:${caseKey}`) : null
    const nested = (caseSteps ?? []).reduce(
      (count, child) => count + 1 + countDescendantSteps(child),
      0,
    )
    dispatchFlowStepsOp('removeSwitchCase', [currentPath, caseKey])
    if (nested > 0) {
      toast.warning(`Deleted case "${caseKey}" and ${nested} nested step(s) — ${SHORTCUT.undo} to undo`)
    }
  }

  const handleDelete = () => {
    const path: StepPath | null =
      selectedStepPath ?? (selectedStepIndex != null ? ([selectedStepIndex] as [number]) : null)
    if (path == null || deleteDisabled) return
    // Blast-radius BEFORE dispatch: a structural delete destroys the whole
    // branch subtree in one click — announce it. The undo hint is real now
    // (F4), so no blocking confirm.
    const nested = countDescendantSteps(step)
    if (path.length === 1) {
      dispatchFlowStepsOp('removeStep', [path[0]])
    } else {
      // Any-depth nested path — removeNestedStep resolves it recursively.
      dispatchFlowStepsOp('removeNestedStep', [path])
    }
    // Selection clearing stays SYNCHRONOUS with the dispatch (the mutator
    // runs synchronously inside execute(); setSelectedStepPath(null) also
    // clears the legacy index mirror).
    setSelectedStepPath(null)
    if (nested > 0) {
      toast.warning(
        `Deleted ${step.type ?? 'basic'} step and ${nested} nested step(s) — ${SHORTCUT.undo} to undo`,
      )
    } else {
      toast.success(`Step deleted — ${SHORTCUT.undo} to undo`)
    }
  }

  // Phase 378 Plan 02 Task 1: derive a nodeType-like value so the condition-add
  // affordance can be revealed when the currently-selected step is a
  // conditional. The inspector reads `step.type` which is 'conditional' for
  // ConditionalStep (matches selectedNode?.nodeType === 'conditional' intent).
  const isConditionalStep = step.type === 'conditional'

  const handleConditionBranchAdd = () => {
    if (!isConditionalStep) return
    const path = selectedStepPath ?? (selectedStepIndex != null ? [selectedStepIndex] as [number] : null)
    if (path == null || path.length !== 1) return
    const parentIdx = path[0]
    // Append a new basic toast step to the conditional's then[] branch
    const current = step as ConditionalStep
    const branchLen = (current.then ?? []).length
    dispatchFlowStepsOp('insertNestedStep', [
      [parentIdx, 'then', branchLen],
      makeConditionBranchStubStep(),
    ])
  }

  return (
    <div data-testid="step-inspector" className="flex flex-col gap-3 p-3">
      {/* Header */}
      <div className="flex items-center gap-2">
        <div className={cn(INSPECTOR.badgeClass, categoryColor)}>
          <StepIcon size={ICON.md} className="text-white" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-xs font-semibold truncate">{label}</div>
          <div className="flex items-center gap-1.5 mt-0.5">
            <span className={cn('inline-flex items-center px-1.5 py-0.5 rounded text-[9px] font-medium', categoryColor)}>
              {category}
            </span>
            {/* Persisted basic steps (and the panel's nodes-fallback seeding)
                often omit type — they are action-bearing basic steps. */}
            <span className="text-[10px] text-muted-foreground">{step.type ?? 'basic'}</span>
          </div>
        </div>
        <button
          className="p-1 text-muted-foreground hover:text-destructive rounded transition-colors disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:text-muted-foreground"
          onClick={handleDelete}
          disabled={deleteDisabled}
          data-testid="step-inspector-delete"
          title={deleteDisabled ? 'A saga must retain at least one stage' : 'Delete step'}
        >
          <Trash2 size={14} />
        </button>
      </div>

      {/* Step info */}
      <div className={INSPECTOR.infoRowClass}>
        <div className="flex justify-between">
          <span>Index</span>
          <span className="font-mono">
            {selectedStepPath != null
              ? selectedStepPath.length === 1
                ? selectedStepPath[0]
                : `${selectedStepPath[0]}.${selectedStepPath[1]}.${selectedStepPath[2]}`
              : selectedStepIndex}
          </span>
        </div>
        <div className="flex justify-between">
          <span>Type</span>
          <span>{step.type ?? 'basic'}</span>
        </div>
        {'action' in step && (
          <div className="flex justify-between">
            <span>Action</span>
            <span>{(step as BasicStep).action}</span>
          </div>
        )}
      </div>

      {/* QA-4: legacy action notice — honest runtime-skip status + explicit
          migration affordance. Display + deliberate action only: rendering
          this never writes; the button performs the SAME single-step
          migration an edit-commit would. */}
      {legacyAlias && (
        <div
          data-testid="legacy-action-notice"
          className="rounded-md border border-amber-500/40 bg-amber-500/10 px-2.5 py-2 space-y-1.5"
        >
          <div className="flex items-center gap-1.5 flex-wrap">
            <span className="text-[10px] font-semibold text-amber-700 dark:text-amber-400">
              Legacy action format
            </span>
            {legacySkipped && (
              <span
                data-testid="legacy-runtime-skip-chip"
                title={`The web runtime has no executor for "${legacyAlias.legacyAction}" — this step is skipped in Preview until it is updated.`}
                className="inline-flex items-center px-1.5 py-0.5 rounded bg-amber-500/20 text-[9px] font-medium text-amber-700 dark:text-amber-400"
              >
                Skipped on web until updated
              </span>
            )}
          </div>
          <p className="text-[10px] text-muted-foreground">
            This step uses the legacy name{' '}
            <span className="font-mono">{legacyAlias.legacyAction}</span>. Saving an edit below
            updates it to <span className="font-mono">{legacyAlias.canonicalAction}</span>.
          </p>
          <button
            type="button"
            data-testid="legacy-action-migrate"
            className="text-[10px] rounded border border-amber-500/40 px-1.5 py-0.5 text-amber-700 dark:text-amber-400 hover:bg-amber-500/20 transition-colors"
            onClick={() =>
              // `?? {}` covers both a missing params property AND one
              // explicitly set to undefined — migrateParams destructures.
              handleMigrate(
                legacyAlias.canonicalAction,
                legacyAlias.migrateParams((step as BasicStep).params ?? {}),
              )
            }
          >
            Update to current format
          </button>
        </div>
      )}

      {/* F17: deferred-skip notice — a legacy name the web runtime silently
          skips that has NO canonical migrate target (no LEGACY_ACTION_ALIASES
          entry). Honest skip chip only: no "Update to current format" button,
          because there is nothing to migrate to. Mutually exclusive with the
          legacy notice above (legacyAlias is undefined for deferred names). */}
      {deferredSkipped && !legacyAlias && (
        <div
          data-testid="deferred-action-notice"
          className="rounded-md border border-amber-500/40 bg-amber-500/10 px-2.5 py-2 space-y-1.5"
        >
          <div className="flex items-center gap-1.5 flex-wrap">
            <span className="text-[10px] font-semibold text-amber-700 dark:text-amber-400">
              Legacy action format
            </span>
            <span
              data-testid="deferred-runtime-skip-chip"
              title={`The web runtime has no executor for "${stepAction}" — this step is skipped in Preview until it is updated.`}
              className="inline-flex items-center px-1.5 py-0.5 rounded bg-amber-500/20 text-[9px] font-medium text-amber-700 dark:text-amber-400"
            >
              Skipped on web until updated
            </span>
          </div>
          <p className="text-[10px] text-muted-foreground">
            This step uses the legacy name{' '}
            <span className="font-mono">{stepAction}</span>, which the web runtime
            does not run yet. It is skipped in Preview until the action is updated.
          </p>
        </div>
      )}

      {/* Parameters form */}
      <div>
        <div className={cn(INSPECTOR.sectionHeaderClass, 'mb-2')}>
          Parameters
        </div>
        {/* key remounts the form when the selection moves — the form
            components seed useState from params once, so reusing the same
            instance across two same-type steps shows (and on blur writes)
            the previous step's values into the newly selected step. */}
        <StepFormDispatcher
          key={`${selectedFlowId ?? ''}:${JSON.stringify(selectedStepPath ?? [selectedStepIndex])}`}
          step={step}
          onChange={handleParamsChange}
          onFieldsChange={handleFieldsChange}
          onAddCase={handleAddCase}
          onRenameCase={handleRenameCase}
          onRemoveCase={handleRemoveCase}
          onMigrate={handleMigrate}
        />
      </div>

      {/*
        Phase 378 Plan 02 Task 1 Part B — Condition Add Affordance.
        Rendered only when the selected step is a conditional. Clicking the
        button appends a new basic step to the conditional's then[] branch.
        Exposes data-testid='step-inspector-condition-add' for Plan 02 E2E
        'condition add' test. Minimal behavior addition (the only behavior
        change allowed by Task 1).
      */}
      {isConditionalStep && (
        <button
          type="button"
          data-testid="step-inspector-condition-add"
          onClick={handleConditionBranchAdd}
          className="w-full text-[11px] rounded-md border border-border bg-muted/40 hover:bg-accent px-2 py-1.5 text-muted-foreground hover:text-foreground transition-colors"
        >
          + Add condition branch step
        </button>
      )}
    </div>
  )
}
